import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Button,
  TextField,
  Typography,
} from "@mui/material";
import IndeterminateCheckBoxOutlinedIcon from "@mui/icons-material/IndeterminateCheckBoxOutlined";
import Delete from "@mui/icons-material/Delete";
import { styled } from "@mui/system";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { fetchHealthFacilitiesWithPackage } from "../../action";
import TableN from "../../reusableComponents/components/generics/TableN";
import _ from "lodash";

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  "& .MuiAccordionSummary-content": {
    margin: "0px !important",
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  ...theme.paper.header,
  fontSize: 600,
  padding: ".8rem",
}));

const EXAMINATION_ACT_TYPE_LABEL = "examination";
const MEDECINE_ACT_TYPE_LABEL = "medecine";

export const PreAuthServiceAct = ({
  onDataChange,
  claimData,
  updateTotalClaimed,
  updateTMvalue,
  disabled,
  updateSupportTotal,
}) => {
  const SignInSuccess = useSelector((state) => state);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [totalClaimed, setTotalClaimed] = useState(0);

  const healthData =
    SignInSuccess?.fosaReducer?.pickerHealthFacilityInsurees?.[0];
  const healthFacilityUuid = claimData?.healthFacility?.uuid || healthData?.uuid;
  const healthSupport =
    SignInSuccess?.fosaReducer?.healthFacilityHealthSupport ?? 100;

  useEffect(() => {
    if (healthFacilityUuid) {
      dispatch(fetchHealthFacilitiesWithPackage(healthFacilityUuid, ["first:50"]));
    }
  }, [dispatch, healthFacilityUuid]);

  useEffect(() => {
    if (SignInSuccess?.fosaReducer?.healthFacilitiesPackageData) {
      const acts = SignInSuccess.fosaReducer.healthFacilitiesPackageData?.filter((act) => act?.actM2m?.apStatus === "REQUIS" && (act?.actM2m?.actType?.label || "").toLowerCase() !==
        EXAMINATION_ACT_TYPE_LABEL.toLowerCase());
      setFilteredServices(acts || []);
    }
  }, [SignInSuccess?.fosaReducer?.healthFacilitiesPackageData]);


  useEffect(() => {
    const actsSource = claimData?.acts ?? claimData?.paActs ?? [];
    const actTypeActs = actsSource.filter((actItem) => {
      const label = (actItem?.act?.actType?.label || "").toLowerCase();
      return (
        label !== EXAMINATION_ACT_TYPE_LABEL.toLowerCase() &&
        label !== MEDECINE_ACT_TYPE_LABEL.toLowerCase()
      );
    });
    if (actTypeActs.length > 0) {
      const initialRows = actTypeActs.map((actItem) => {
        const jsonExt = JSON.parse(actItem.jsonExt || actItem.json_ext || "{}");
        const act = actItem.act;
        const fullCode =
          act?.fullCode || act?.full_code || act?.code || act?.label || "";
        return {
          actId: act?.uuid || act?.id,
          service: fullCode,
          price:
            actItem.priceAsked ||
            act?.priceHomologated ||
            act?.price_homologated,
          quantity: actItem.qtyProvided,
          priceAsked: actItem.priceAsked,
          countedPrice: jsonExt.countedPrice,
          tmValue: jsonExt.tmValue,
          explanation: actItem.explanation,
        };
      });
      setRows(initialRows);
      setSelectedServices(initialRows.map((row) => row.actId).filter(Boolean));
      calculateTotalClaimed(initialRows);
      onDataChange(initialRows);
    } else if (
      !claimData?.paActs &&
      claimData?.services?.length > 0 &&
      claimData.services.some((s) => s?.service?.categoryType === "A")
    ) {
      const actServices = claimData.services.filter(
        (s) => s?.service?.categoryType === "A"
      );
      const initialRows = actServices.map((service) => {
        const jsonExt = JSON.parse(service?.jsonExt || "{}");
        const svc = service.service;
        const fullCode = svc?.fullCode ?? svc?.full_code ?? svc?.code ?? svc?.name ?? "";
        return {
          actId: svc?.uuid ?? svc?.id,
          service: fullCode,
          price: service.priceAsked ?? svc?.price,
          quantity: service.qtyProvided,
          priceAsked: service.priceAsked,
          countedPrice: jsonExt.countedPrice,
          tmValue: jsonExt.tmValue,
          explanation: service.explanation,
        };
      });
      setRows(initialRows);
      setSelectedServices(initialRows.map((row) => row.actId).filter(Boolean));
      calculateTotalClaimed(initialRows);
      onDataChange(initialRows);
    } else {
      setRows([
        {
          actId: "",
          service: "",
          quantity: "",
          price: "",
          tmValue: "",
          countedPrice: "",
          explanation: "",
          priceAsked: "",
        },
      ]);
    }
  }, [claimData]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const handleServiceChange = (index, option) => {
    if (!option) return;

    const selectedAct = filteredServices.find(
      (item) => (item?.actM2m?.uuid || item?.actM2m?.id) === option.id
    );
    if (!selectedAct?.actM2m) return;

    const actM2m = selectedAct.actM2m;
    const price = Number(
      selectedAct.agreedPrice ?? actM2m.priceHomologated ?? 0
    );
    const healthSupportPct = Number(healthSupport) || 100;
    const countedPricePerUnit = _.round(
      price * (healthSupportPct / 100),
      2
    );
    const tmValuePerUnit = _.round(
      price * ((100 - healthSupportPct) / 100),
      2
    );

    const fullCode = actM2m.fullCode || actM2m.full_code || "";

    const newRows = rows.map((row, i) =>
      i === index
        ? {
          ...row,
          actId: actM2m.uuid || actM2m.id,
          service: fullCode,
          price,
          quantity: 1,
          priceAsked: price,
          countedPrice: String(countedPricePerUnit),
          tmValue: String(tmValuePerUnit),
        }
        : row
    );
    setRows(newRows);
    onDataChange(newRows);
    const newSelectedServices = newRows
      .map((row) => row.actId)
      .filter((s) => s !== "");
    setSelectedServices(newSelectedServices);
    calculateTotalClaimed(newRows);
  };

  const handleChange = (index, event) => {
    const { name, value } = event.target;
    const newRows = rows.map((row, i) =>
      i === index ? { ...row, [name]: value } : row
    );
    setRows(newRows);
    onDataChange(newRows);
    if (name === "quantity") {
      calculateTotalClaimed(newRows);
    }
  };

  const addRow = () => {
    setRows([
      ...rows,
      {
        actId: "",
        service: "",
        quantity: "",
        price: "",
        tmValue: "",
        countedPrice: "",
        explanation: "",
        priceAsked: "",
      },
    ]);
  };

  const deleteRow = (index) => {
    const newRows = rows.filter((_, i) => i !== index);
    setRows(newRows);
    const newSelectedServices = newRows
      .map((row) => row.actId)
      .filter((id) => id !== "");
    setSelectedServices(newSelectedServices);
    calculateTotalClaimed(newRows);
    onDataChange(newRows);
  };

  const getActOption = (item) => {
    const actM2m = item?.actM2m;
    const fullCode = actM2m?.fullCode || actM2m?.full_code || "";
    return {
      ...item,
      label: fullCode,
      id: actM2m?.uuid || actM2m?.id,
    };
  };

  const getAvailableActs = (currentActId) => {
    return filteredServices.filter(
      (item) =>
        !selectedServices.includes(item?.actM2m?.uuid || item?.actM2m?.id) ||
        (item?.actM2m?.uuid || item?.actM2m?.id) === currentActId
    );
  };

  const headers = () => [
    t("Service"),
    t("Quantity"),
    t("Price"),
    t("TicketModerateur"),
    t("CountedPrice"),
    t("Action"),
  ];

  const calculateTotalClaimed = (rows) => {
    const total = rows.reduce(
      (sum, row) => sum + (Number(row.quantity) || 0) * (Number(row.price) || 0),
      0
    );
    const totalTm = rows.reduce((sum, row) => {
      const quantity = row.quantity || 1;
      const tmValue = row.tmValue ? parseFloat(row.tmValue) * quantity : 0;
      return sum + tmValue;
    }, 0);

    const totalSupport = rows.reduce((sum, row) => {
      const quantity = row.quantity || 1;
      const countedPrice = row.countedPrice ? row.countedPrice : null;
      return sum + (countedPrice ? parseFloat(countedPrice) * quantity : 0);
    }, 0);

    setTotalClaimed(total);
    updateTMvalue?.(totalTm);
    updateTotalClaimed?.(total);
    updateSupportTotal?.(totalSupport);
  };

  const totalTmValueRounded = _.round(
    rows.reduce((sum, r) => {
      const quantity = r.quantity || 1;
      const tmValue = r.tmValue ? r.tmValue : null;
      return sum + (tmValue ? parseFloat(tmValue) * quantity : 0);
    }, 0),
    2
  );

  const totalCounterPriceValue = _.round(
    rows.reduce((sum, r) => {
      const quantity = r.quantity || 1;
      const countedPrice = r.countedPrice ? r.countedPrice : null;
      return (
        sum + (countedPrice ? parseFloat(countedPrice) * quantity : 0)
      );
    }, 0),
    2
  );

  const multiplyVal = (value, multiplyBy) => {
    return parseFloat(value || 0) * parseFloat(multiplyBy || 1);
  };

  const itemFormatters = () => [
    (row, index) => {
      const availableOptions = getAvailableActs(row.actId).map(getActOption);
      const selectedOption = row.actId
        ? availableOptions.find((opt) => opt.id === row.actId)
        : null;
      const displayValue =
        selectedOption ??
        (row.actId && row.service
          ? { id: row.actId, label: row.service }
          : null);
      return (
        <Autocomplete
          options={availableOptions}
          getOptionLabel={(opt) => opt?.label ?? ""}
          isOptionEqualToValue={(opt, val) =>
            opt?.id != null &&
            val?.id != null &&
            String(opt.id) === String(val.id)
          }
          sx={{ width: 200 }}
          size="small"
          value={displayValue}
          disabled={disabled}
          onChange={(e, option) => {
            handleServiceChange(index, option);
          }}
          renderInput={(params) => <TextField {...params} />}
        />
      );
    },
    (row, index) => (
      <TextField
        className="no-arrow"
        type="number"
        name="quantity"
        size="small"
        value={row?.quantity ?? ""}
        onChange={(e) => handleChange(index, e)}
        disabled={disabled}
      />
    ),
    (row) => (
      <TextField
        type="text"
        name="price"
        value={
          row.price != null && row.price !== ""
            ? _.round(
              Number(row.price) * (Number(row.quantity) || 1),
              2
            )
            : ""
        }
        size="small"
        disabled={true}
      />
    ),
    (row) => (
      <TextField
        type="text"
        name="ticket"
        size="small"
        value={
          row.tmValue ? multiplyVal(row.tmValue, row.quantity || 1) : ""
        }
        disabled={true}
      />
    ),
    (row) => (
      <TextField
        type="text"
        name="countedPrice"
        size="small"
        value={
          row.countedPrice
            ? multiplyVal(row.countedPrice, row.quantity || 1)
            : ""
        }
        disabled={true}
      />
    ),
    (row, index) =>
      disabled ? (
        <div />
      ) : (
        <Button
          startIcon={<Delete style={{ color: "red" }} />}
          onClick={() => deleteRow(index)}
        />
      ),
  ];

  const preHeaders = [
    "\u200b",
    "",
    totalClaimed > 0 ? t("Total Claimed: ") + totalClaimed : "",
    totalTmValueRounded > 0 ? t("Total TM: ") + totalTmValueRounded : "",
    totalCounterPriceValue > 0
      ? t("Total Supported by Camu: ") + totalCounterPriceValue
      : "",
    " ",
    " ",
  ];

  return (
    <StyledAccordion>
      <StyledAccordionSummary
        expandIcon={<IndeterminateCheckBoxOutlinedIcon />}
        aria-controls="panel1-content"
        id="panel1-header"
      >
        <Typography variant="h6">{t("ServicesAct")}</Typography>
      </StyledAccordionSummary>
      <AccordionDetails>
        <TableN
          headers={headers()}
          itemFormatters={itemFormatters()}
          preHeaders={preHeaders}
          items={rows}
          withSelection={"single"}
          withPagination={false}
        />
        {!disabled && <Button onClick={addRow}>{t("Add")}</Button>}
      </AccordionDetails>
    </StyledAccordion>
  );
};
